import DataTable from 'Components/DataTable';
import FormRow from 'Components/FormRow';
import InputHandler from 'Components/InputHandler';
import BlockWrapper from 'Components/_NewElements/BlockWrapper';
import { gds15 } from 'neuro-schemas';
import { formatPartialDate } from 'Utility/date';
import * as React from 'react';
import { useMyServiceContext } from '../..';
import colors from '../../../../../config/theme/colors';
import type { Theme } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import InfoPopover from 'Components/InfoPopover';
import { isNumber } from 'neuro-utils';
import browserLanguage from 'Utility/browserLanguage';
import { useAppSelector } from 'Store/index';

const {
  calculateProgress,
  utils: { mapGDS15ScoreToInterpretation },
} = gds15;

const gds15Fields: Array<keyof SafeOmit<IGDS15, 'date'>> = [
  'satisfiedWithLife',
  'droppedActivities',
  'lifeIsEmpty',
  'oftenBored',
  'goodSpirits',
  'afraidSomethingBad',
  'happyMostTime',
  'oftenHelpless',
  'preferStayHome',
  'memoryProblems',
  'wonderfulToLive',
  'worthless',
  'fullOfEnergy',
  'hopelessSituation',
  'othersBetterOff',
];

const InterpretationRow = ({
  title,
  value,
}: {
  title?: string | number;
  value?: string | number | React.JSX.Element;
}): React.JSX.Element => {
  // Track page width and breakpoints
  const mdOrSmaller = useMediaQuery((theme: Theme) => theme.breakpoints.down(1401));

  return (
    <tr
      style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '2rem',
        fontWeight: 600,
        color: colors.tertiaryText,
      }}
    >
      <td>
        <div style={{ width: mdOrSmaller ? '40rem' : '70rem', paddingLeft: '0.5rem' }}>{title}</div>
      </td>
      <td>
        <div style={{ width: '20rem', paddingLeft: '0.5rem' }}>{value}</div>
      </td>
    </tr>
  );
};

const Interpretation = ({
  scoreTitle,
  yielded,
  ceiling,
  interpretationTitle,
  interpretation,
}: {
  scoreTitle: string;
  yielded: number | React.JSX.Element;
  ceiling: number | null;
  interpretationTitle: string;
  interpretation: string;
}): React.JSX.Element => {
  return (
    <table style={{ width: '100%', borderSpacing: '0px' }}>
      <tbody>
        <InterpretationRow
          title={scoreTitle}
          value={
            <span>
              {yielded} / {ceiling}
            </span>
          }
        />
        <InterpretationRow title={interpretationTitle} value={interpretation} />
      </tbody>
    </table>
  );
};

export const GDS15 = (): React.JSX.Element => {
  const locale = useAppSelector(s => s.settings.userSettings.uiLanguage) ?? browserLanguage;
  const { editing, setEditingData, viewing, setViewingObj, setEditingObj } = useMyServiceContext<'gds15'>();
  const gds15Data = editing?.data ?? viewing?.data ?? {};
  const gds15Localizations = gds15.getLocalizations(locale);

  const onChangeSurveyForm = (values: TOnChangeValues): void => {
    const field = Object.keys(values)[0];
    if (!field) return;
    const value = Object.values(values)[0];
    setEditingData?.({
      ...gds15Data,
      [field]: value,
    });
  };

  // Track page width and breakpoints
  const mdOrSmaller = useMediaQuery((theme: Theme) => theme.breakpoints.down(1401));

  const { yielded: score, ceiling } = calculateProgress(gds15Data);

  const interpretation = isNumber(score)
    ? gds15Localizations[`interpretation.${mapGDS15ScoreToInterpretation(score)}`]
    : undefined;

  return (
    <>
      {!viewing ? (
        <div style={{ width: '80%', color: colors.tertiaryText, marginBottom: '2rem' }}>
          <p>{gds15Localizations['surveyInfo']}</p>
        </div>
      ) : null}
      <BlockWrapper
        title={viewing ? formatPartialDate(gds15Data.date) : undefined}
        buttons={
          viewing
            ? [
                {
                  title: 'general.edit',
                  onClick: () => {
                    setViewingObj(null);
                    setEditingObj({ type: 'gds15', data: gds15Data });
                  },
                },
              ]
            : undefined
        }
      >
        {!viewing && (
          <FormRow title={'general.date'} headerWidth={5.3}>
            <InputHandler
              name="date"
              type="PartialDate"
              dateDefault="now"
              formData={{
                document: { date: gds15Data.date },
                onChange: onChangeSurveyForm,
              }}
              editing={!!editing}
            />
          </FormRow>
        )}
        <div style={{ width: mdOrSmaller ? '100%' : '60%', marginBottom: '2rem' }}>
          <DataTable
            headers={viewing ? ['', '', ''] : ['', '']}
            data={{
              rowData: gds15Fields.map(f => {
                const rowSet = [
                  <div key={f + 'div1'} style={{ width: mdOrSmaller ? '40rem' : '70rem' }}>
                    {gds15Localizations[f]}
                  </div>,
                  <div key={f + 'div2'} style={{ width: '20rem' }}>
                    <InputHandler
                      key={f}
                      name={f}
                      type="Radio"
                      editing={!!editing}
                      formData={{
                        document: { [f]: gds15Data[f] },
                        onChange: onChangeSurveyForm,
                      }}
                      options={['yes', 'no']}
                      optionFormatter={o => gds15Localizations[`opts.${o}`] ?? '-'}
                    />
                  </div>,
                ];
                return rowSet;
              }),
              rowStyle: viewing ? undefined : gds15Fields.map(() => ({ rowAlign: 'top' })),
            }}
          />
        </div>
        <div style={{ width: mdOrSmaller ? '100%' : '60%' }}>
          <Interpretation
            scoreTitle={gds15Localizations['score']}
            yielded={
              isNumber(score) ? (
                score
              ) : (
                <InfoPopover
                  text="general.notFilledInformative"
                  color="primary"
                  values={['', ...gds15Fields]
                    .filter(f => (f === '' ? true : !gds15Data[f as (typeof gds15Fields)[0]]))
                    .map(f => gds15Localizations[f])
                    .join('\n')}
                />
              )
            }
            ceiling={ceiling}
            interpretationTitle={gds15Localizations['interpretationCategory']}
            interpretation={interpretation ?? '-'}
          />
        </div>
      </BlockWrapper>
      <div style={{ color: colors.tertiaryText, fontStyle: 'italic' }}>
        <p>{gds15Localizations['reference']}</p>
      </div>
    </>
  );
};
